import { useEffect, useMemo, useState } from "react";
import gravatar from "gravatar";
import {
  AnketaResourcesEnum,
  isValidEmail,
  KeycloakGroup,
  KeycloakUser,
  useAnketaAuth,
  useApi,
  useToast,
} from "../../../../";
import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiCard,
  EuiText,
  EuiSpacer,
  EuiAvatar,
  EuiIcon,
  EuiLink,
} from "@elastic/eui";
import { ComplexQuestionEditorProps } from "../../../";
import { useAnketa } from "../../context_provider";
import { useParams } from "react-router";
import { useAnketaI18n } from "src/internationalization";

interface KeycloakUserEditor extends ComplexQuestionEditorProps {
  type: "sales" | "technical" | "customer";
}

export const ComplexQuestionUserEditor = ({
  question,
  setValue,
  invalid,
  type,
}: KeycloakUserEditor): JSX.Element => {
  const { keycloak } = useAnketaAuth();
  const ctx = useAnketa();
  const { Get } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();
  const params = useParams();

  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<KeycloakUser[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<
    EuiComboBoxOptionOption<KeycloakUser>[]
  >([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const canFetch = useMemo<boolean>(() => {
    if (keycloak) {
      if (typeof keycloak.token === "string") {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [keycloak]);

  // The whole contact group - the options to pick from. Internal-only on the
  // server; null when denied so the caller can fall back.
  const getGroupContactPersons = async (): Promise<KeycloakUser[] | null> => {
    const { success, data } = await Get({
      path: `v2/${AnketaResourcesEnum.opsanio}/keycloak/groups/${type}`,
      responseType: "json",
      token: keycloak.token,
    });
    if (!success) return null;
    // An empty 200 collapses to data=null (AnketaApiResponse), i.e. no members.
    const group = Array.isArray(data) ? (data as KeycloakGroup[])[0] : undefined;
    return group?.members ?? [];
  };

  // Only the contacts this questionnaire names, for its members - how a
  // customer, who may not read the group, still sees who their contacts are.
  const getSelectedContactPersons = async (): Promise<KeycloakUser[] | null> => {
    if (!params.catalogId || !params.questionnaireId) return null;
    const { success, data } = await Get<Array<KeycloakUser>>({
      path: `v2/${AnketaResourcesEnum.questionnaires}/${params.catalogId}/${params.questionnaireId}/contacts/${type}`,
      responseType: "json",
      token: keycloak.token,
    });
    if (!success) return null;
    return Array.isArray(data) ? data : [];
  };

  const getContactPersons = async (): Promise<KeycloakUser[]> => {
    // A read-only field needs no options, only the selected people.
    const [primary, fallback] = question.readonly
      ? [getSelectedContactPersons, getGroupContactPersons]
      : [getGroupContactPersons, getSelectedContactPersons];
    return (await primary()) ?? (await fallback()) ?? [];
  };

  const getCustomerContactPersons = async (): Promise<KeycloakUser[]> => {
    const { data } = await Get<Array<KeycloakUser>>({
      path: `v2/${AnketaResourcesEnum.questionnaires}/${params.catalogId}/${params.questionnaireId}/organizations/customers`,
      responseType: "json",
      token: keycloak.token,
    });
    return Array.isArray(data) ? data : [];
  };

  const userOptions = useMemo<EuiComboBoxOptionOption<KeycloakUser>[]>(() => {
    return users.map((user) => ({
      label:
        `${user.firstName || ""} ${user.lastName || ""} (${user.email})`.trim(),
      value: user,
    }));
  }, [users]);

  const onChange = (
    selectedOptions: EuiComboBoxOptionOption<KeycloakUser>[],
  ) => {
    setSelectedOptions(selectedOptions);
    setSelectedUsers(selectedOptions.map((opt) => opt.value));
    setValue(selectedOptions.map((opt) => opt.value.id).join(","));
  };

  const getUserInitials = (user: KeycloakUser): string => {
    const first = user.firstName?.charAt(0) || "";
    const last = user.lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || user.email.charAt(0).toUpperCase();
  };

  const getUserAttribute = (
    user: KeycloakUser,
    attributeName: string,
  ): string | undefined => {
    const attributes = user.attributes;
    if (attributes && attributes[attributeName]) {
      return attributes[attributeName];
    }

    const schema = user.profileSchema as any;
    if (schema && schema[attributeName]) {
      return schema[attributeName];
    }

    return undefined;
  };

  const renderUserCard = (user: KeycloakUser) => {
    const jobTitle = getUserAttribute(user, "jobTitle");
    const businessPhone = getUserAttribute(user, "businessPhone");
    const fullName =
      `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email;

    return (
      <EuiCard
        layout="horizontal"
        icon={
          <EuiAvatar
            name={getUserInitials(user)}
            size="xl"
            color="#006BB4"
            imageUrl={gravatar.url(
              user?.email && user?.email ? user?.email : "",
            )}
          />
        }
        title={
          <EuiFlexGroup direction="column" gutterSize="none">
            <EuiFlexItem>
              <EuiText size="m">{fullName}</EuiText>
            </EuiFlexItem>
            {jobTitle && (
              <EuiFlexItem>
                <EuiFlexGroup gutterSize="xs" alignItems="center">
                  <EuiFlexItem grow={false}>
                    <EuiIcon type="users" size="s" />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiText size="xs">{jobTitle}</EuiText>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        }
        description={
          <div style={{ marginTop: "8px" }}>
            <EuiFlexGroup direction="column" gutterSize="s">
              <EuiFlexItem>
                <EuiFlexGroup gutterSize="xs" alignItems="center">
                  <EuiFlexItem grow={false}>
                    <EuiIcon type="email" size="m" />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiLink href={`mailto:${user.email}`}>
                      <EuiText size="s">{user.email}</EuiText>
                    </EuiLink>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiFlexItem>

              {businessPhone && (
                <EuiFlexItem>
                  <EuiFlexGroup gutterSize="xs" alignItems="center">
                    <EuiFlexItem grow={false}>
                      <EuiIcon type="mobile" size="m" />
                    </EuiFlexItem>
                    <EuiFlexItem>
                      <EuiLink href={`tel:${businessPhone}`}>
                        <EuiText size="s">{businessPhone}</EuiText>
                      </EuiLink>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </div>
        }
      />
    );
  };

  const dummyKeycloakUser = (email: string): KeycloakUser => {
    return {
      id: email,
      email: email,
      username: email,
      createdTimestamp: Date.now(),
      emailVerified: false,
      enabled: true,
      requiredActions: [],
      disableableCredentialTypes: [],
      federatedIdentities: [],
      notBefore: 0,
      totp: false,
      access: {
        manageGroupMembership: false,
        view: true,
        mapRoles: false,
        impersonate: false,
        manage: false,
      },
      userProfileMetadata: {
        attributes: {
          readOnly: true,
          displayName: "dummy",
          multivalued: false,
          name: "dummy",
          required: false,
          validations: {},
        },
        groups: {
          readOnly: true,
          displayName: "dummy",
          multivalued: false,
          name: "dummy",
          required: false,
          validations: {},
        },
      },
    };
  };

  const onCreateOption = (searchValue: string = "") => {
    if (type !== "customer") {
      return;
    }
    const normalizedSearchValue = searchValue.trim().toLowerCase();
    const validEmail = isValidEmail(searchValue);

    if (!normalizedSearchValue || !validEmail) {
      setIsInvalid(true);
      addToast({
        title: translate(
          "anketa.questionnaire.editor.user.createFailed.title",
          { defaultMessage: "Creation failed" },
        ),
        color: "danger",
        text: translate("anketa.questionnaire.editor.user.createFailed.text", {
          defaultMessage: "Input is not a valid e-mail address!",
        }),
      });
      return;
    }

    const newOption: EuiComboBoxOptionOption<KeycloakUser> = {
      label: searchValue,
      value: dummyKeycloakUser(searchValue),
    };

    const updatedOptions = [...selectedOptions, newOption];
    const updatedUsers = [...(selectedUsers ?? []), newOption.value];

    setSelectedOptions(updatedOptions);
    setSelectedUsers(updatedUsers);
    setValue(updatedUsers.map((u) => u.id).join(","));
    setIsInvalid(false);
  };

  // Set once the user list request has finished, whatever its outcome - the
  // spinner must not wait for a non-empty list that a denied or empty lookup
  // never delivers.
  const [isFetched, setIsFetched] = useState<boolean>(false);

  useEffect(() => {
    if (!canFetch) return;
    let cancelled = false;
    (type !== "customer" ? getContactPersons() : getCustomerContactPersons())
      .catch((error) => {
        console.error("Error fetching contact persons:", error);
        return [] as KeycloakUser[];
      })
      .then((list) => {
        if (cancelled) return;
        setUsers(list);
        setIsFetched(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch]);

  useEffect(() => {
    if (!isLoaded && isFetched) {
      const val = question.fact.getStrValue(ctx.i18nContext) ?? "";

      const userIdArray = val
        .split(",")
        .map((id) => id.trim())
        .filter((id) => !isValidEmail(id))
        .filter(Boolean);

      const emailArray = val
        .split(",")
        .map((id) => id.trim())
        .filter((id) => isValidEmail(id))
        .filter(Boolean);

      const foundUsersOverID = userIdArray
        .map((userId) => users.find((user) => user.id === userId))
        .filter((user): user is KeycloakUser => Boolean(user));

      const foundUsersOverEmail = emailArray
        .map((email) => dummyKeycloakUser(email))
        .filter((user): user is KeycloakUser => Boolean(user));

      const foundUsers = [...foundUsersOverID, ...foundUsersOverEmail];
      const options = foundUsers.map((user) => ({
        label:
          `${user.firstName || ""} ${user.lastName || ""} (${user.email})`.trim(),
        value: user,
      }));

      setSelectedOptions(options);
      setSelectedUsers(foundUsers);

      setIsLoaded(true);
    }
  }, [users, isFetched, isLoaded, question, ctx]);

  return (
    <EuiFlexItem
      grow
      style={{
        minWidth: "200px",
      }}
    >
      <EuiComboBox
        placeholder={translate(
          "anketa.questionnaire.editor.user.select.placeholder",
          { values: { type }, defaultMessage: "Select {{type}} users" },
        )}
        options={userOptions}
        selectedOptions={selectedOptions}
        onChange={onChange}
        isClearable={true}
        clearOnBlur={true}
        isLoading={!isLoaded}
        isDisabled={question.readonly}
        onCreateOption={onCreateOption}
        isInvalid={isInvalid}
      />

      <EuiSpacer size="l" />

      {selectedUsers.length > 0 && (
        <EuiFlexGroup direction="column" gutterSize="m">
          {selectedUsers.map((user) => (
            <EuiFlexItem key={user.id}>{renderUserCard(user)}</EuiFlexItem>
          ))}
        </EuiFlexGroup>
      )}
    </EuiFlexItem>
  );
};
